import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  input,
  ChangeDetectionStrategy,
  signal,
  viewChild,
  afterRenderEffect,
} from '@angular/core';
import { Engine, Runner, Render, World, Bodies, Events } from 'matter-js';
import { PlinkoService } from '../plinko.service';
import {
  DropBallOptions,
  PlinkoMultiplier,
  PlinkoResult,
} from '../interfaces/plinko.interface';
import { UIService } from '@core/services/ui.service';
import { SessionService } from '@core/services/session.service';
import { DecimalPipe } from '@angular/common';
import { NgpTooltip, NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { CostComponent } from '@core/components/cost/cost.component';
import { SoundsService } from '@core/services/sounds.service';

interface ExBody extends Matter.Body {
  data?: any;
  version?: number;
}

@Component({
  selector: 'app-plinko-board',
  templateUrl: './plinko-board.component.html',
  styleUrls: ['./plinko-board.component.scss'],
  standalone: true,
  imports: [DecimalPipe, NgpTooltip, NgpTooltipTrigger, CostComponent],
  host: {
    class: 'box-black-third rounded-6',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlinkoBoardComponent {
  readonly rows = input(8);
  canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  multipliers = viewChild.required<ElementRef<HTMLDivElement>>('multipliers');

  engine!: Engine;
  render!: Render;
  runner!: Runner;
  width = 700;
  height = 700;
  spaceBetweenPegs = 0;
  firstPegX = signal(0);
  pegRadius = 0;
  elementWidth = 700;

  history = signal<
    {
      multiplier: PlinkoMultiplier;
      result: PlinkoResult;
    }[]
  >([]);
  _speedInterval: any;

  constructor(
    public plinkoService: PlinkoService,
    public uiService: UIService,
    private soundsService: SoundsService,
    private cd: ChangeDetectorRef,
    public session: SessionService
  ) {
    afterRenderEffect(() => {
      if (
        (this.plinkoService.currentRows() ||
          this.plinkoService.currentRisk() ||
          this.plinkoService.animSpeed()) &&
        this.plinkoService.currentMultipliers().length > 0
      ) {
        this.setupPlinko();
      }
    });

    afterRenderEffect(() => {
      const lastBall = this.plinkoService.balls().at(-1);

      if (lastBall) {
        this.dropBall(lastBall);
      }
    });
  }

  onResize() {
    this.elementWidth = this.canvas().nativeElement.clientWidth;
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    clearInterval(this._speedInterval);
    this.plinkoService.running.set(false);

    if (this.engine) {
      this.clearWorld();
      Engine.clear(this.engine);
      Render.stop(this.render);
      Runner.stop(this.runner);
    }
  }

  clearWorld() {
    if (!this.engine?.world) {
      return;
    }

    const balls: ExBody[] = this.engine.world.bodies.filter(
      (body) => body.label === 'ball'
    );
    this.engine.world.bodies = this.engine.world.bodies.filter(
      (body) => body.label !== 'ball'
    );
    const ballsSum = balls.reduce((acc, ball) => {
      return acc + (ball.data?.result?.winningValue ?? 0);
    }, 0);

    if (ballsSum > 0) {
      const currBalance = this.session.balanceType();
      this.session.balance.update((prev) => {
        return {
          ...prev,
          [currBalance]: prev[currBalance] + ballsSum,
        };
      });
    }

    World.clear(this.engine.world, false);
  }

  async ngAfterViewInit() {
    this.engine = Engine.create();
    this.render = Render.create({
      canvas: this.canvas().nativeElement,
      engine: this.engine,
      options: {
        width: this.width,
        height: this.height,
        wireframes: false,
        showAxes: false,
        showBounds: false,
        showCollisions: false,
        background: 'transperent',
      },
    });

    this.runner = Runner.create({
      isFixed: true,
    });

    this.engine.timing.timeScale = 0.4;
    Render.run(this.render);
    Runner.run(this.runner, this.engine);

    Events.on(this.engine, 'collisionStart', (event) => {
      for (const pair of event.pairs) {
        if (pair.bodyA.label === 'ground' || pair.bodyB.label === 'ground') {
          const ball = pair.bodyA.label === 'ground' ? pair.bodyB : pair.bodyA;
          this.removeBall(ball);
        } else if (pair.bodyA.label === 'peg' || pair.bodyB.label === 'peg') {
          const peg = pair.bodyA.isStatic ? pair.bodyA : pair.bodyB;
          this.pegHit(peg);
        } else if (
          (pair.bodyA.label === 'pocket' && pair.bodyB.label === 'ball') ||
          (pair.bodyB.label === 'pocket' && pair.bodyA.label === 'ball')
        ) {
          const pocket = pair.bodyA.isStatic ? pair.bodyA : pair.bodyB;
          const ball = pair.bodyA === pocket ? pair.bodyB : pair.bodyA;

          // console.log('🚀 ~ REAL:', (pocket as any).data.pocketNum);
          // console.log(
          //   '🚀 ~ BACKEND FROM BOARD:',
          //   (ball as any).data.result.pocket
          // );
          this.removeBall(ball);
          this.pocketHit(pocket, ball);
        }
      }
    });

    setTimeout(() => {
      this.setupPlinko();
    });
  }

  setupPlinko() {
    if (!this.engine) {
      return;
    }

    this.clearWorld();
    const entities = [];
    this.createPegs(this.plinkoService.currentRows());
    this.createPockets(this.plinkoService.currentRows());

    const ground: ExBody = Bodies.rectangle(
      this.width / 2,
      this.height - 40,
      this.width * 2,
      20,
      {
        isStatic: true,
        label: 'ground',
        render: {
          fillStyle: 'transparent',
          strokeStyle: 'transparent',
        },
      }
    );

    entities.push(ground);

    World.add(this.engine.world, entities);
    this.elementWidth = this.canvas().nativeElement.clientWidth;

    if (this._speedInterval) {
      clearInterval(this._speedInterval);
    }
    this._speedInterval = setInterval(() => {
      if (this.plinkoService.animSpeed().toFixed(0) === '1') {
        return;
      }

      for (
        let i = 0;
        i < parseInt(this.plinkoService.animSpeed().toFixed(0)) - 1;
        i++
      ) {
        Engine.update(this.engine, 1000 / 60);
      }
    }, 1000 / 60);
  }

  createPegs(rows = 13) {
    this.pegRadius = Math.max(16 - 0.8 * rows, 4);
    this.spaceBetweenPegs = (this.width / rows) * 0.8;
    const pegs = [];
    for (let row = 3; row < rows + 3; row++) {
      for (let col = 0; col < row; col++) {
        const o = this.width - (rows - 1) * this.spaceBetweenPegs;
        const x =
          ((rows - row) * this.spaceBetweenPegs) / 2 +
          this.spaceBetweenPegs * col +
          o / 2;
        if (row === rows + 2 && col === 0) {
          this.firstPegX.set(x);
        }
        const y =
          this.height - (rows + 3 - row - 1) * this.spaceBetweenPegs - 100;
        const peg = Bodies.circle(x, y, this.pegRadius, {
          label: 'peg',
          isStatic: true,
          render: {
            fillStyle: '#868686',
            strokeStyle: '#868686',
            lineWidth: 2,
          },
        });
        pegs.push(peg);
      }
    }

    World.add(this.engine.world, pegs);
  }

  createPockets(rows = 13) {
    const pockets = [];
    for (let pocket = 0; pocket < rows + 1; pocket++) {
      const o = 2 * this.pegRadius;
      const width = this.spaceBetweenPegs - o;
      const height = 5;
      const row = rows + 2;
      const x =
        ((rows - row) * this.spaceBetweenPegs) / 2 +
        this.spaceBetweenPegs * pocket +
        (this.width - (rows - 1) * this.spaceBetweenPegs) / 2 +
        this.spaceBetweenPegs / 2;
      const y = this.height - 70;

      const rectangle: ExBody = Bodies.rectangle(x, y, width, height, {
        isStatic: true,
        label: 'pocket',
        render: {
          fillStyle: 'transparent',
          strokeStyle: 'transparent',
        },
        //restitution: 1,
      });

      rectangle.data = {
        pocketNum: pocket,
        multiplier: this.plinkoService.currentMultipliers()[pocket],
      };

      pockets.push(rectangle);
    }

    World.add(this.engine.world, pockets);
  }

  dropBall(options: DropBallOptions) {
    if (options.point === -1) {
      options.point = this.getRandomPoint();
    }

    const scale = Math.max(
      1.7 - 0.09 * (this.plinkoService.currentRows() - 8),
      0.6
    );

    const ball: ExBody = Bodies.circle(options.point!, 30, this.pegRadius * 2, {
      render: {
        sprite: {
          texture: `assets/old-images/plinko/ball.png`,
          xScale: scale,
          yScale: scale,
        },
        opacity: options?.opacity ?? 1,
      },
      label: 'ball',
      mass: 3.5,
      density: 0.01,
      restitution: 0.3,
      collisionFilter: {
        group: -1,
      },
    });

    ball.data = options;

    World.add(this.engine.world, ball);
  }

  getRandomPoint() {
    const min = this.width / 2 - this.spaceBetweenPegs + 0.01;
    const max = this.width / 2 + this.spaceBetweenPegs - 0.01;
    return Math.random() * (max - min) + min;
  }

  removeBall(ball: ExBody) {
    setTimeout(() => {
      World.remove(this.engine.world, ball);
    }, 0);
  }

  pegHit(peg: ExBody) {
    if (!peg?.version) {
      peg.version = 0;
    }
    const version = ++peg.version;

    peg.render.sprite!.texture = `assets/old-images/plinko/peg.png`;
    const scale = Math.max(
      1.1 - 0.15 * (this.plinkoService.currentRows() - 8),
      0.5
    );
    peg.render.sprite!.yScale = scale;
    peg.render.sprite!.xScale = scale;

    setTimeout(() => {
      const peg2: ExBody | undefined = this.engine.world.bodies.find(
        (body) => body.id === peg.id
      );
      if (!peg2 || version !== peg2?.version) {
        return;
      }
      peg.render.sprite!.texture = '';
    }, 200);
  }

  pocketHit(pocket: ExBody, ball: ExBody) {
    const { pocketNum, multiplier } = pocket.data;
    const { result, hit } = ball.data;

    if (hit) {
      return;
    }

    const multiplierElement =
      this.multipliers().nativeElement.children[pocketNum];
    multiplierElement.classList.add('animate');
    setTimeout(() => {
      multiplierElement.classList.remove('animate');
    }, 300);

    ball.data.hit = true;
    const currBalance = this.session.balanceType();
    this.session.balance.update((prev) => {
      return {
        ...prev,
        [currBalance]: prev[currBalance] + result.winningValue,
      };
    });
    this.history.update((prev) => {
      const total = [
        {
          multiplier: multiplier,
          result: result,
        },
        ...prev,
      ];
      return total.slice(0, this.uiService.breakpoints().max_lg ? 4 : 8);
    });

    let sound = 'plinko-low';
    let volume = 1;
    if (multiplier.tier === 2) {
      sound = 'plinko-med';
    } else if (multiplier.tier === 3) {
      sound = 'plinko-high';
      volume = 0.7;
    }

    this.soundsService.playSound(sound, {
      extension: 'mp3',
      maxVolume: volume,
    });
  }
}
